import { Button as CommonButton, Icon } from 'components/common';
import styled, { css } from 'styled-components';
import { colours } from 'utils/theme';

const __textReset = css`
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 22px;
  color: ${colours.character.primary85};
`;

export const H2 = styled.h2`
  font-size: 16px;
  font-weight: 500;
  font-style: medium;
  ${__textReset}
`;

export const H3 = styled.h3`
  font-size: 14px;
  font-weight: 700;
  ${__textReset}
`;

export const Paragraph = styled.p`
  font-size: 14px;
  font-weight: 400;
  ${__textReset}
`;

export const ErrorBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: ${p => (p.$alignTop ? 'flex-start' : 'center')};
  justify-content: flex-start;
  border-radius: 2px;
  padding: 9px 16px;
  border: 1px solid #ffe58f;
  background: #fffbe6;
  margin-top: 12px;
  font-size: 12px;
  gap: 12px;
`;

/** Generic "we can't act on this query" message: bold heading, then sub-text and
 *  advice as plain lines. Scoped to NLPAPIErrorBox's generic branch (QueryRejected /
 *  UnsupportedFeature) — single-line error copy renders without this structure. */
export const GenericMessageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

export const GenericMessageHeading = styled.strong`
  font-weight: 600;
  font-size: 14px;
  color: ${colours.character.primary85};
`;

export const GenericMessageSubText = styled.span`
  font-size: 13px;
  color: ${colours.character.primary85};
`;

export const GenericMessageAdvice = styled.span`
  font-size: 13px;
  color: ${colours.character.primary85};

  &::before {
    content: '• ';
  }
`;

/** Daily quota or short-window rate limit (SCIL-14004): pink banner below the search bar — not a placeholder watermark. */
export const SciSearchLimitBanner = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  border-radius: 2px;
  padding: 9px 16px;
  border: 1px solid #ffccc7;
  background: #fff1f0;
  margin-top: 12px;
  font-size: 13px;
  gap: 10px;
  line-height: 22px;
  color: ${colours.character.primary85};
`;

export const ErrorIcon = styled(Icon)`
  width: 14px;
  height: 14px;
  font-weight: normal;
  margin-top: ${p => (p.$alignTop ? '4px' : '1px')};
`;

export const SpellCheckBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 2px;
  padding: 9px 16px;
  border: 1px solid #ffe58f;
  background: #fffbe6;
  gap: 10px;
  font-size: 12px;
`;

/** Stacks yellow warning boxes (spellcheck + entity chooser) with a small gap
 *  between them and the expected 12px gap from the preceding element. */
export const PreCheckBoxStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 12px;
`;

export const SpellCheckMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: normal;
`;

export const SpellCheckSuggestionButton = styled.button`
  padding: 3px 14px;
  border: 1px solid #d9d9d9;
  border-radius: 16px;
  background: #f5f5f5;
  color: ${colours.character.primary85};
  cursor: pointer;
  font-size: 12px;
  line-height: 20px;
  transition: all 0.2s;

  &:hover {
    border-color: #bfbfbf;
    background: #fafafa;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const BetaBadge = styled.span`
  background-color: ${colours.infoLightBlue};
  color: ${colours.blue};
  padding: 0px 12px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 13px;
`;

/** Wraps label + count so only the number uses a pill (SciSearch header spec). */
export const DemoCreditsRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const DemoCreditsLabel = styled.span`
  font-size: 14px;
  line-height: 22px;
  color: ${colours.character.primary85};
  white-space: nowrap;
`;

/** Count only: light blue pill with blue digits (not one mega-pill around label + count). */
export const DemoCreditsCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  padding: 2px 10px;
  border-radius: 999px;
  background-color: ${colours.infoLightBlue};
  color: ${colours.blue};
  font-weight: 600;
  font-size: 13px;
  line-height: 20px;
`;

export const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 24px;
`;

export const TitleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const TitleSpacer = styled.div`
  margin-left: auto;
`;

export const StageTwoFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// Compact one-line conflict warning that sits in the footer just left of Search,
// so it stays visible when the review list is scrolled (SCIL-16099).
export const FooterConflictWarning = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin-right: 12px;
  padding: 6px 10px;
  border-radius: 2px;
  border: 1px solid #ffe58f;
  background: #fffbe6;
  font-size: 12px;
  line-height: 1.3;
  color: ${colours.character.primary85};
`;

export const SearchRow = styled.div`
  position: relative;
  width: 100%;
`;

export const SearchControlsAnchor = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 32px;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SearchCharacterCount = styled.span`
  font-size: 14px;
  line-height: 22px;
  color: ${p => (p.$overLimit ? colours.red : colours.grey)};
  user-select: none;
  white-space: nowrap;
`;

export const SearchButtonInside = styled(CommonButton)`
  width: auto;
  height: 32px;
  border-radius: 0 2px 2px 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const SuggestionsGrid = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

export const SuggestionSection = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  border: 1px solid ${colours.borderGrey};
  border-radius: 4px;
  padding: 16px;
  box-sizing: border-box;
  overflow-y: auto;
`;

export const StageOneRecentAndExamplesWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 280px;
  overflow: hidden;
`;

/** Stage-two scroll region: everything between the sticky query box at the top and the action buttons at the bottom. */
export const StageTwoScrollArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  margin-right: -4px;
`;

export const SectionTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 22px;
  margin-bottom: 16px;
`;

export const EmptyState = styled.div`
  color: #8c8c8c;
  font-size: 14px;
  line-height: 22px;
  padding: 8px 0;
`;

export const PillsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

/** Column of left-aligned pills spanning the section width; each pill sizes to its own content. */
export const SuggestionPillsContainer = styled(PillsContainer)`
  align-items: flex-start;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

/** Single scroll region for pinned + unpinned recent items (PRD: 50 rows + scrollbar). Kept short so the modal fits the viewport without page scroll. */
export const RecentSearchesScrollArea = styled.div`
  max-height: min(195px, 28vh);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  margin-right: -4px;
  -webkit-overflow-scrolling: touch;
`;

export const PinnedSection = styled.div`
  position: relative;
`;

export const PinnedWatermark = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  color: ${colours.character.disabled};
  opacity: 0.38;
  font-size: 10px;
  letter-spacing: 1.2px;
  font-weight: 600;
  text-transform: uppercase;
  pointer-events: none;
  z-index: 1;
`;

export const PinnedSeparator = styled.hr`
  border: none;
  border-top: 1px solid ${colours.borderGrey};
  margin: 8px 0;
  width: 100%;
`;

/** Example prompt shown as guidance only: nothing to click, so it wraps instead of truncating. */
export const ExampleChip = styled.div`
  box-sizing: border-box;
  max-width: 100%;
  padding: 6px 12px;
  border: 1px solid ${colours.borderGrey};
  border-radius: 20px;
  background-color: #f5f5f5;
  color: ${colours.character.primary85};
  font-size: 12px;
  line-height: 14px;
  text-align: left;
  white-space: normal;
  overflow-wrap: break-word;
  cursor: default;
  transition: all 0.2s;

  &:hover {
    border-color: ${colours.grey};
    background-color: #ebebeb;
  }
`;

export const DebugContainer = styled.pre`
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 16px;
  max-height: 400px;
  overflow: auto;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const FeedbackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 4px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;

  &:hover {
    border-color: #40a9ff;
    color: #40a9ff;
  }
`;

export const ReviewQueryBox = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  padding: 9px 12px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: ${colours.character.primary85};
`;

export const ReviewDescription = styled.p`
  margin-bottom: 16px;
  color: #595959;
`;

export const ReviewAccordionTitle = styled.span`
  flex: 1;
  display: flex;
  gap: 8px;
`;

export const ReviewCountBadge = styled.span`
  display: flex;
  align-items: center;
  margin-top: 1px;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  background: ${colours.infoLightBlue};
  color: ${colours.blue};
  font-size: 12px;
  line-height: 20px;
  font-weight: 600;
`;

export const ReviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 0;
`;

export const ReviewSectionTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
`;

export const ReviewRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

export const ReviewField = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: #262626;
`;

export const ReviewValues = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const ReviewValueChip = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  border: 1px solid #56b3c3;
  border-radius: 2px;
  padding: 1px 8px;
  background: #56b3c31a;
  color: ${colours.character.primary85};
  font-size: 12px;
  line-height: 20px;
`;

export const IgnoredReviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: default;
  /* Content-width, so the tooltip anchors on the filter and not on the panel. */
  width: fit-content;
  max-width: 100%;

  /* The warning icon is nudged down for the top-aligned error box, and an
     inline SVG sits on the text baseline; neither suits a centred row. */
  svg {
    margin-top: 0;
    display: block;
    flex-shrink: 0;
  }

  /* Paragraph carries its own colour, so the row's is never inherited. */
  p {
    color: #bfbfbf;
  }
`;

export const IgnoredValueChip = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  padding: 1px 8px;
  background: #f5f5f5;
  color: #8c8c8c;
  font-size: 12px;
  line-height: 20px;
`;

export const ReviewValueChipExcluded = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  border: 1px solid ${colours.lightRed};
  border-radius: 2px;
  padding: 1px 8px;
  background: ${colours.excludeFilter};
  color: ${colours.character.primary85};
  font-size: 12px;
  line-height: 20px;
`;

export const ReviewEmptyText = styled.div`
  color: #8c8c8c;
  font-size: 13px;
  line-height: 22px;
`;

// This is a span due to accordions setting the title CSS to force align-items to center
// if the given title is a div. This is a workaround.
export const SynonymAccordionTitle = styled.span`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 8px;
`;

export const SynonymAccordionText = styled.span`
  display: flex;
  min-width: 0;
  flex-direction: column;
`;

// Fixed-width left slot so the icon/toggle is centred and every row's text
// starts at the same x regardless of the control's natural width.
export const AccordionLeadSlot = styled.span`
  width: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Lead slot width (32) + the title row's gap (8). Accordion bodies are indented
// by this so their content lines up with the title text rather than with the
// icon/toggle sitting in the lead slot.
export const ACCORDION_BODY_INDENT = 40;

export const SynonymAccordionHeader = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SynonymTermAccordionTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TermChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const TermChip = styled.button.withConfig({
  shouldForwardProp: prop =>
    prop !== 'selected' && prop !== 'excluded' && prop !== 'conflict',
})`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 24px;
  border-radius: 2px;
  padding: 1px 8px;
  font-size: 12px;
  line-height: 20px;
  cursor: pointer;
  transition: all 0.15s;
  background: transparent;

  ${p => {
    // A term selected on BOTH sides is marked amber to match the conflict warning
    // (SCIL-16099) — takes precedence over the include/exclude colours.
    if (p.conflict) {
      return `
    border: 1px solid ${colours.gold};
    background: #fffbe6;
    color: ${colours.character.primary85};
    &:hover { background: #fff1b8; }
  `;
    }
    if (p.selected) {
      // Selected exclusion chips go red to mirror the top review chips (SCIL-16099).
      return p.excluded
        ? `
    border: 1px solid ${colours.lightRed};
    background: ${colours.excludeFilter};
    color: ${colours.character.primary85};
    &:hover { filter: brightness(0.97); }
  `
        : `
    border: 1px solid #56b3c3;
    background: #56b3c31a;
    color: ${colours.character.primary85};
    &:hover { background: #56b3c330; }
  `;
    }
    return `
    border: 1px solid #d9d9d9;
    color: #8c8c8c;
    &:hover { border-color: ${
      p.excluded ? colours.lightRed : '#56b3c3'
    }; color: ${colours.character.primary85}; }
  `;
  }}

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    &:hover {
      border-color: #d9d9d9;
      color: #8c8c8c;
      background: transparent;
    }
  }
`;

export const DisclaimerLabel = styled.label`
  font-size: 13px;
  line-height: 12px;
  color: ${colours.grey};
`;
